import { useEffect } from 'react'

import { useLocalStorageState } from '@/hooks/useLocalStorageState'
import type { RecordGroupDetail } from '@/types/api/record-group'

import { RecordTreeNode } from './RecordTreeNode'

export function RecordFileExplorer({
  group,
  currentGroupId,
  activeGroupPathIds,
  lockedResourceIds,
}: {
  /** Top-level group, kept at the top while its descendants are navigated. */
  group: RecordGroupDetail
  currentGroupId: string
  activeGroupPathIds: string[]
  lockedResourceIds: Set<string>
}) {
  const [expandedGroupIds, setExpandedGroupIds] = useLocalStorageState<string[]>(
    `recordGroupExplorer:${group.id}:expanded`,
    [group.id],
  )
  const activePathKey = activeGroupPathIds.join('|')

  useEffect(() => {
    const next = [...new Set([...expandedGroupIds, ...activeGroupPathIds])]
    if (next.length !== expandedGroupIds.length) setExpandedGroupIds(next)
    // Expanded groups are merged when the route path changes, while explicit chevron actions
    // remain free to collapse them until the next navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePathKey])

  const toggleExpanded = (groupId: string) => {
    setExpandedGroupIds(
      expandedGroupIds.includes(groupId)
        ? expandedGroupIds.filter((id) => id !== groupId)
        : [...expandedGroupIds, groupId],
    )
  }

  const collapseChildGroups = () => setExpandedGroupIds([group.id])
  const collapseFolder = (groupId: string) => {
    setExpandedGroupIds(expandedGroupIds.filter((id) => id !== groupId))
  }
  const treeState = {
    currentGroupId,
    lockedResourceIds,
    expandedGroupIds,
    onToggleExpanded: toggleExpanded,
    onCollapseFolder: collapseFolder,
    onCollapseChildGroups: collapseChildGroups,
  }

  return (
    <div className="min-h-0 overflow-y-auto" data-testid="record-file-explorer">
      <div role="tree" aria-label={`Groups under ${group.name}`}>
        <RecordTreeNode
          group={group}
          depth={0}
          treeState={treeState}
          isMainParent
        />
      </div>
    </div>
  )
}
